import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Folder, 
  File, 
  ChevronRight, 
  ChevronDown, 
  ExternalLink, 
  FolderOpen,
  Grid3X3,
  List,
  Search,
  Plus,
  FolderPlus,
  Upload,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FilePen,
  Loader2,
  Trash2,
  Eye
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface Document {
  id: string;
  name: string;
  description: string | null;
  folder_path: string | null;
  google_drive_url: string | null;
  google_drive_file_id: string | null;
  mime_type: string | null;
  file_size: number | null;
  created_at: string;
}

interface FolderNode {
  name: string;
  path: string;
  documents: Document[];
  subfolders: Record<string, FolderNode>;
}

interface DocumentsTabContentProps {
  ptaId: string | null;
  isReadOnly: boolean;
}

// Get file icon based on mime type
const getFileIcon = (mimeType: string | null) => {
  if (!mimeType) return <FileText className="h-5 w-5 text-muted-foreground" />;
  
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  }
  if (mimeType.includes('document') || mimeType.includes('word')) {
    return <FilePen className="h-5 w-5 text-blue-600" />;
  }
  if (mimeType.includes('image')) {
    return <FileImage className="h-5 w-5 text-purple-600" />;
  }
  if (mimeType.includes('video')) {
    return <FileVideo className="h-5 w-5 text-pink-600" />;
  }
  if (mimeType.includes('audio')) {
    return <FileAudio className="h-5 w-5 text-orange-600" />;
  }
  if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) {
    return <FileArchive className="h-5 w-5 text-yellow-600" />;
  }
  if (mimeType.includes('pdf')) {
    return <FileText className="h-5 w-5 text-red-600" />;
  }
  return <FileText className="h-5 w-5 text-muted-foreground" />;
};

// Folder icon component with Windows-style appearance
const FolderIcon = ({ isOpen, className }: { isOpen?: boolean; className?: string }) => (
  <div className={cn("relative", className)}>
    {isOpen ? (
      <FolderOpen className="h-6 w-6 text-amber-500 drop-shadow-sm" />
    ) : (
      <Folder className="h-6 w-6 text-amber-500 drop-shadow-sm" />
    )}
  </div>
);

export function DocumentsTabContent({ ptaId, isReadOnly }: DocumentsTabContentProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [viewMode, setViewMode] = useState<'folder' | 'list'>('folder');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPath, setCurrentPath] = useState('/');
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  // Auto-sync on mount and periodic refresh
  useEffect(() => {
    if (ptaId) {
      checkPtaAdmin();
      // Initial sync on mount
      performSync(true);
      
      // Set up periodic sync every 60 seconds
      syncIntervalRef.current = setInterval(() => {
        performSync(false);
      }, 60000);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [ptaId]);

  const checkPtaAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !ptaId) return;

    const { data } = await supabase
      .from('members')
      .select('is_pta_admin')
      .eq('user_id', user.id)
      .eq('pta_id', ptaId)
      .maybeSingle();

    setIsPtaAdmin(data?.is_pta_admin || false);
  };

  const fetchDocuments = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('pta_documents')
      .select('*')
      .eq('pta_id', ptaId)
      .order('folder_path')
      .order('name');

    if (!error && data) {
      setDocuments(data);
    }
    setLoading(false);
  };

  // Build folder tree structure
  const buildFolderTree = (): FolderNode => {
    const root: FolderNode = {
      name: 'Documents',
      path: '/',
      documents: [],
      subfolders: {}
    };

    documents.forEach(doc => {
      const folderPath = doc.folder_path || '/';
      const pathParts = folderPath.split('/').filter(Boolean);
      
      let current = root;
      let currentPathBuilder = '/';

      // Build folder structure
      pathParts.forEach(part => {
        currentPathBuilder = currentPathBuilder === '/' ? `/${part}` : `${currentPathBuilder}/${part}`;
        if (!current.subfolders[part]) {
          current.subfolders[part] = {
            name: part,
            path: currentPathBuilder,
            documents: [],
            subfolders: {}
          };
        }
        current = current.subfolders[part];
      });

      // Add document to the correct folder
      if (folderPath === '/') {
        root.documents.push(doc);
      } else {
        current.documents.push(doc);
      }
    });

    return root;
  };

  const toggleFolder = (path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const selectFolder = (path: string) => {
    setCurrentPath(path);
    // Also expand the folder when selecting it
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.add(path);
      return next;
    });
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(query) ||
      (doc.description && doc.description.toLowerCase().includes(query))
    );
  }, [documents, searchQuery]);

  // Get current folder contents
  const getCurrentFolderContents = () => {
    const tree = buildFolderTree();
    if (currentPath === '/') return tree;
    
    const parts = currentPath.split('/').filter(Boolean);
    let current = tree;
    for (const part of parts) {
      if (current.subfolders[part]) {
        current = current.subfolders[part];
      }
    }
    return current;
  };

  // Can user add folders at current path?
  const canAddFolder = () => {
    if (isReadOnly) return false;
    if (currentPath === '/') return isPtaAdmin;
    return true; // Non-root, write access users can add subfolders
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !ptaId) return;

    setCreatingFolder(true);
    
    // Create a placeholder document to represent the folder
    const folderPath = currentPath === '/' ? `/${newFolderName.trim()}` : `${currentPath}/${newFolderName.trim()}`;
    
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('pta_documents')
      .insert({
        pta_id: ptaId,
        name: '.folder',
        folder_path: folderPath,
        uploaded_by: user?.id
      });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to create folder",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Folder created",
        description: `Created folder "${newFolderName.trim()}"`
      });
      setNewFolderName('');
      setNewFolderDialogOpen(false);
      fetchDocuments();
    }
    setCreatingFolder(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !ptaId) return;

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(selectedFile);
      const fileContent = await base64Promise;

      const { data, error } = await supabase.functions.invoke('google-drive', {
        body: {
          action: 'upload',
          ptaId,
          fileName: selectedFile.name,
          fileContent,
          mimeType: selectedFile.type || 'application/octet-stream',
          folderPath: currentPath,
          description: uploadDescription,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "File uploaded",
        description: `"${selectedFile.name}" uploaded to Google Drive`
      });

      setSelectedFile(null);
      setUploadDescription('');
      setUploadDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchDocuments();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDocument = (doc: Document) => {
    if (doc.google_drive_url) {
      window.open(doc.google_drive_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Perform sync with Google Drive (silent = no toast on success for periodic syncs)
  const performSync = async (showLoadingState: boolean = true) => {
    if (!ptaId || syncing) return;
    
    if (showLoadingState) {
      setSyncing(true);
      setLoading(true);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('google-drive', {
        body: {
          action: 'sync',
          ptaId,
        },
      });

      if (error) {
        // Silent fail for background syncs if Drive not connected
        if (error.message?.includes('Google Drive not connected')) {
          // Just fetch local docs instead
          await fetchDocuments();
          return;
        }
        throw error;
      }

      if (data?.error) {
        if (data.error.includes('Google Drive not connected')) {
          await fetchDocuments();
          return;
        }
        throw new Error(data.error);
      }

      setLastSyncTime(new Date());
      
      // Refresh local documents after sync
      await fetchDocuments();
      
      // Only show toast on initial load if changes were made
      if (showLoadingState && (data.addedCount > 0 || data.removedCount > 0 || data.updatedCount > 0)) {
        toast({
          title: "Synced with Google Drive",
          description: `Added: ${data.addedCount}, Removed: ${data.removedCount}, Updated: ${data.updatedCount}`
        });
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      // Only show error toast on initial load
      if (showLoadingState) {
        toast({
          title: "Sync issue",
          description: "Could not sync with Google Drive. Showing cached documents.",
          variant: "destructive"
        });
        await fetchDocuments();
      }
    } finally {
      if (showLoadingState) {
        setSyncing(false);
        setLoading(false);
      }
    }
  };

  const canUpload = () => {
    if (isReadOnly) return false;
    if (currentPath === '/') return isPtaAdmin;
    return true; // Non-root, write access users can upload
  };

  // Breadcrumb navigation
  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    const crumbs = [{ name: 'Documents', path: '/' }];
    let buildPath = '';
    parts.forEach(part => {
      buildPath += `/${part}`;
      crumbs.push({ name: part, path: buildPath });
    });
    return crumbs;
  };

  const renderFolderView = (folder: FolderNode, depth: number = 0) => {
    const isExpanded = expandedFolders.has(folder.path);
    const isSelected = currentPath === folder.path;
    const hasContent = folder.documents.filter(d => d.name !== '.folder').length > 0 || Object.keys(folder.subfolders).length > 0;
    
    return (
      <div key={folder.path}>
        <div
          className={cn(
            "w-full flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors text-left group cursor-pointer",
            isSelected && "bg-sky-50 dark:bg-sky-950/30 ring-1 ring-sky-200 dark:ring-sky-800"
          )}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
          onClick={() => selectFolder(folder.path)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(folder.path);
            }}
            className="w-4 flex items-center justify-center hover:bg-muted rounded"
          >
            {hasContent ? (
              isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )
            ) : (
              <span className="w-4" />
            )}
          </button>
          <FolderIcon isOpen={isExpanded} />
          <span className="font-medium flex-1">{folder.name}</span>
          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
            {folder.documents.filter(d => d.name !== '.folder').length} files
          </span>
          {isSelected && !isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Upload className="h-4 w-4 mr-1" />
              Upload
            </Button>
          )}
        </div>

        {isExpanded && (
          <div>
            {/* Subfolders */}
            {Object.values(folder.subfolders).map(subfolder => 
              renderFolderView(subfolder, depth + 1)
            )}
            
            {/* Documents (exclude .folder placeholders) */}
            {folder.documents.filter(d => d.name !== '.folder').map(doc => (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors group cursor-pointer"
                style={{ paddingLeft: `${(depth + 1) * 20 + 12}px` }}
                onClick={() => handleOpenDocument(doc)}
              >
                <span className="w-4" />
                {getFileIcon(doc.mime_type)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{doc.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(doc.created_at), 'MMM d, yyyy')}
                    {doc.file_size && ` • ${formatFileSize(doc.file_size)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {doc.google_drive_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDocument(doc);
                      }}
                      title="Open in Google Drive"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderListView = () => {
    const docsToShow = searchQuery ? filteredDocuments : documents;
    const visibleDocs = docsToShow.filter(d => d.name !== '.folder');

    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr,150px,100px,60px] gap-4 p-3 bg-muted/50 border-b border-border text-sm font-medium text-muted-foreground">
          <span>Name</span>
          <span>Modified</span>
          <span>Size</span>
          <span></span>
        </div>
        <div className="divide-y divide-border">
          {visibleDocs.length > 0 ? (
            visibleDocs.map(doc => (
              <div
                key={doc.id}
                className="grid grid-cols-[1fr,150px,100px,60px] gap-4 p-3 hover:bg-accent transition-colors items-center group cursor-pointer"
                onClick={() => handleOpenDocument(doc)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {getFileIcon(doc.mime_type)}
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{doc.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{doc.folder_path || '/'}</p>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(doc.created_at), 'MMM d, yyyy')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {formatFileSize(doc.file_size)}
                </span>
                <div className="flex items-center justify-end">
                  {doc.google_drive_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDocument(doc);
                      }}
                      title="Open in Google Drive"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              {searchQuery ? 'No documents match your search' : 'No documents found'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const folderTree = buildFolderTree();

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading documents...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>PTA Documents</CardTitle>
            <CardDescription>
              {isReadOnly ? 'Browse shared PTA documents (read-only)' : 'Browse and manage shared PTA documents'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center border border-border rounded-lg p-1">
              <Button
                variant={viewMode === 'folder' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-3"
                onClick={() => setViewMode('folder')}
              >
                <Folder className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-3"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Syncing indicator */}
            {syncing && (
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing...
              </span>
            )}
            
            {/* Add folder button */}
            {canAddFolder() && (
              <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderPlus className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Folder</DialogTitle>
                    <DialogDescription>
                      {currentPath === '/' 
                        ? 'Create a new root folder (Admin only)' 
                        : `Create a subfolder in "${currentPath}"`}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="folder-name">Folder Name</Label>
                      <Input
                        id="folder-name"
                        placeholder="Enter folder name"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newFolderName.trim()) {
                            handleCreateFolder();
                          }
                        }}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setNewFolderDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateFolder} 
                        disabled={!newFolderName.trim() || creatingFolder}
                      >
                        {creatingFolder ? 'Creating...' : 'Create Folder'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Upload button */}
            {canUpload() && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Upload dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setSelectedFile(null);
            setUploadDescription('');
            if (fileInputRef.current) fileInputRef.current.value = '';
          }
          setUploadDialogOpen(open);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Upload "{selectedFile?.name}" to Google Drive{currentPath !== '/' ? ` in folder "${currentPath}"` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                {selectedFile && getFileIcon(selectedFile.type)}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{selectedFile?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFile && formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload-description">Description (optional)</Label>
                <Input
                  id="upload-description"
                  placeholder="Add a description for this file"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload to Drive'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search bar */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent>
        {documents.filter(d => d.name !== '.folder').length > 0 || Object.keys(folderTree.subfolders).length > 0 ? (
          viewMode === 'folder' ? (
            <div className="border border-border rounded-lg bg-card">
              {renderFolderView(folderTree)}
            </div>
          ) : (
            renderListView()
          )
        ) : (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <FolderIcon isOpen={false} className="mx-auto mb-4 scale-150" />
            <p className="text-muted-foreground font-medium">No documents found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Documents synced from Google Drive will appear here
            </p>
            {canAddFolder() && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setNewFolderDialogOpen(true)}
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                Create your first folder
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
